import { ReactNode, CSSProperties } from "react";
import { InputProps as AntdMobileInputProps } from "antd-mobile";
import { BaseComponentProps, Variant, Size } from "../../types";
import { IconVariant } from "../../../Icon/Icon.types";
import { ButtonShape } from "../../../Base/Button/Button.types";

// 前置图标配置
export interface PrefixIconConfig {
  icon: string | ReactNode; // 可以是 icon 名称（字符串）或图片元素（ReactNode）
  onClick?: () => void;
  variant?: IconVariant;
  color?: string;
  size?: number | "sm" | "md" | "lg";
  className?: string;
  // 可选：如果 icon 是字符串但想作为图片路径使用
  isImagePath?: boolean;
  // 可选：标识这是一个需要更多空间的复杂组件（如 CountryCodeSelect）
  isComplexComponent?: boolean;
  /** 与 isComplexComponent 联用：覆盖默认左侧留白（默认 8rem；注册/登录等「区号+|+图标」勿改，提现绑卡仅区号可传如 5.25rem） */
  complexContentPaddingLeft?: string;
}

// 后置按钮配置
export interface SuffixButtonConfig {
  text: string;
  onClick: () => void;
  variant?: Variant;
  size?: Size;
  shape?: ButtonShape;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
}

// 后置单位占位配置
export interface SuffixUnitConfig {
  text: string;
  className?: string;
}

// 后置图标配置
export interface SuffixIconConfig {
  icon: string | ReactNode; // 可以是 icon 名称（字符串）或 ReactNode
  onClick?: () => void;
  variant?: IconVariant;
  className?: string;
  style?: React.CSSProperties;
}

// 后置内容类型
export type SuffixContent =
  | SuffixButtonConfig
  | SuffixUnitConfig
  | SuffixIconConfig;

// 判断是否为按钮配置
export const isSuffixButton = (
  config: SuffixContent
): config is SuffixButtonConfig => {
  return "onClick" in config && !("icon" in config);
};

// 判断是否为单位配置
export const isSuffixUnit = (
  config: SuffixContent
): config is SuffixUnitConfig => {
  return !("onClick" in config) && "text" in config;
};

// 判断是否为图标配置
export const isSuffixIcon = (
  config: SuffixContent
): config is SuffixIconConfig => {
  return "onClick" in config && "icon" in config;
};

export interface EnhancedInputProps
  extends Omit<AntdMobileInputProps, "children" | "className">,
    BaseComponentProps {
  children?: ReactNode;
  variant?: Variant;
  size?: Size;

  // 前置图标配置
  prefixIcon?: PrefixIconConfig;

  // 后置内容配置（按钮或单位）
  suffixContent?: SuffixContent;

  // 后置图标（简化版，直接传入 ReactNode）
  suffixIcon?: ReactNode;

  // 后置图标点击事件
  onSuffixIconClick?: () => void;

  // 自定义样式类名
  className?: string;

  // 输入框内容区域的最小宽度（确保不与前后缀重叠）
  minContentWidth?: string;

  // 追加/覆盖输入框内容区样式（与内部计算的 inputContentStyle 合并）
  inputStyle?: CSSProperties;

  // 错误状态
  error?: boolean;

  // 点击事件
  onClick?: () => void;
}
